import {
  Button,
  FormSelect,
  Input,
  Textarea,
  PhoneInput,
} from '@/components/atoms';
import { FormField } from '@/components/molecules';
import {
  businessDetailsSchema,
  personalDetailsSchema,
  type BusinessDetailsFormData,
  type PersonalDetailsFormData,
} from '@/validation-schema';
import { Controller, useForm } from 'react-hook-form';
import {
  businessTypes,
  businessIndustries,
  countries,
  nigerianStates,
  cities,
} from '@/lib/constants';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import type { ValidationError } from 'yup';
import type {
  RegisteredBusinessVerifyPayload,
  UnregisteredBusinessVerifyPayload,
} from '@/api/mutations/business';

type CombinedFormData = Partial<
  BusinessDetailsFormData & PersonalDetailsFormData
>;

interface KYBFormProps {
  onSubmit: (
    payload: RegisteredBusinessVerifyPayload | UnregisteredBusinessVerifyPayload
  ) => void;
  initialPersonalData?: Partial<PersonalDetailsFormData>;
  /** Prefill and disable form when business is already verified */
  initialBusinessData?: Partial<CombinedFormData>;
  disabled?: boolean;
  isVerifying?: boolean;
  /** When true, submit is hidden/disabled (max verification attempts reached) */
  maxAttemptsReached?: boolean;
}

export const KYBForm = ({
  onSubmit,
  initialPersonalData,
  initialBusinessData,
  disabled = false,
  isVerifying = false,
  maxAttemptsReached = false,
}: KYBFormProps) => {
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    reset,
    formState: { errors },
  } = useForm<CombinedFormData>({
    mode: 'onChange',
    defaultValues:
      disabled &&
      initialBusinessData &&
      Object.keys(initialBusinessData).length > 0
        ? initialBusinessData
        : {},
  });

  const selectedBusinessType = watch('businessType');
  const selectedIndustry = watch('businessIndustry');
  const selectedCountry = watch('country');
  const selectedState = watch('state');
  const selectedLga = watch('lga');
  const profilePrefillAppliedRef = useRef(false);

  // When verified, keep form in sync if initialBusinessData updates (e.g. after refetch)
  useEffect(() => {
    if (
      disabled &&
      initialBusinessData &&
      Object.keys(initialBusinessData).length > 0
    ) {
      reset(initialBusinessData);
    }
  }, [disabled, initialBusinessData, reset]);

  // Prefill personal details when initial data is available (editable form)
  useEffect(() => {
    if (
      !disabled &&
      initialPersonalData &&
      selectedBusinessType === 'unregistered'
    ) {
      if (initialPersonalData.firstName)
        setValue('firstName', initialPersonalData.firstName);
      if (initialPersonalData.lastName)
        setValue('lastName', initialPersonalData.lastName);
      if (initialPersonalData.middleName)
        setValue('middleName', initialPersonalData.middleName);
      if (initialPersonalData.dateOfBirth)
        setValue('dateOfBirth', initialPersonalData.dateOfBirth);
      if (initialPersonalData.emailAddress)
        setValue('emailAddress', initialPersonalData.emailAddress);
      if (initialPersonalData.phoneNumber)
        setValue('phoneNumber', initialPersonalData.phoneNumber);
      if (initialPersonalData.nationality)
        setValue('nationality', initialPersonalData.nationality);
      if (initialPersonalData.nin) setValue('nin', initialPersonalData.nin);
    }
  }, [disabled, initialPersonalData, selectedBusinessType, setValue]);

  // Prefill registered business details from profile on load (editable form)
  useEffect(() => {
    if (disabled || !initialBusinessData) return;
    if (Object.keys(initialBusinessData).length === 0) return;

    if (!profilePrefillAppliedRef.current) {
      reset({
        businessType: initialBusinessData.businessType ?? 'registered',
        ...initialBusinessData,
      });
      profilePrefillAppliedRef.current = true;
      return;
    }

    if (selectedBusinessType !== 'registered') return;

    const prefillFields: (keyof CombinedFormData)[] = [
      'businessName',
      'street',
      'state',
      'customState',
      'lga',
      'customLga',
      'country',
      'customCountry',
      'businessIndustry',
      'businessDescription',
    ];

    prefillFields.forEach((field) => {
      const value = initialBusinessData[field];
      if (value !== undefined && value !== '') {
        setValue(field, value);
      }
    });
  }, [disabled, initialBusinessData, selectedBusinessType, reset, setValue]);

  const handleFormSubmit = async (data: CombinedFormData) => {
    if (!selectedBusinessType) {
      toast.error('Please select a business type.');
      return;
    }

    // Validate based on business type and set field errors so user sees what to fix
    try {
      if (selectedBusinessType === 'registered') {
        await businessDetailsSchema.validate(data, { abortEarly: false });
      } else if (selectedBusinessType === 'unregistered') {
        await personalDetailsSchema.validate(data, { abortEarly: false });
      } else {
        toast.error('Please select a business type.');
        return;
      }
    } catch (err) {
      const validationError = err as ValidationError;
      if (validationError.inner?.length) {
        validationError.inner.forEach((e) => {
          const path = e.path as keyof CombinedFormData;
          if (path) setError(path, { type: 'manual', message: e.message });
        });
      }
      toast.error('Please fix the errors below and try again.');
      return;
    }

    if (selectedBusinessType === 'registered') {
      const resolvedCountry =
        data.country === 'others' ? (data.customCountry ?? '') : data.country;
      const resolvedState =
        data.state === 'others' ? (data.customState ?? '') : data.state;
      const resolvedLga =
        data.lga === 'others' ? (data.customLga ?? '') : data.lga;
      const resolvedIndustry =
        data.businessIndustry === 'others'
          ? (data.customIndustry ?? '')
          : data.businessIndustry;

      const payload: RegisteredBusinessVerifyPayload = {
        idType: 'rc_number',
        idNumber: data.rcNumber || '',
        businessName: data.businessName || '',
        state: resolvedState || '',
        lga: resolvedLga || '',
        country: resolvedCountry || '',
        industry: resolvedIndustry || '',
        description: data.businessDescription || '',
        businessAddress: data.street || '',
        officeAddress: data.officeAddress || data.street || '',
      };
      onSubmit(payload);
    } else if (selectedBusinessType === 'unregistered') {
      const payload: UnregisteredBusinessVerifyPayload = {
        idType: 'nin',
        idNumber: data.nin || '',
        firstName: data.firstName || '',
        middleName: data.middleName,
        lastName: data.lastName || '',
        dateOfBirth: data.dateOfBirth || '',
        email: data.emailAddress || '',
        phoneNumber: data.phoneNumber || '',
        nationality: data.nationality || '',
      };
      onSubmit(payload);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto"
    >
      <FormField
        label="Business type"
        className="md:col-span-2"
        error={errors.businessType?.message}
      >
        <Controller
          name="businessType"
          control={control}
          render={({ field }) => (
            <FormSelect
              options={businessTypes}
              value={field.value}
              onChange={field.onChange}
              placeholder="Choose Type"
              disabled={disabled}
            />
          )}
        />
      </FormField>

      {selectedBusinessType === 'registered' && (
        <>
          <FormField
            label="Business Name"
            className="md:col-span-1"
            error={errors.businessName?.message}
          >
            <Input
              placeholder="Mary Jane Stores"
              {...register('businessName')}
              disabled={disabled}
              readOnly={disabled}
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the exact business name as registered with CAC or your
              official business registration document.
            </p>
          </FormField>

          <FormField
            label="Business Registration Number"
            className="md:col-span-1"
            error={errors.rcNumber?.message}
          >
            <Input
              placeholder="Enter Business Registration Number"
              {...register('rcNumber')}
              disabled={disabled}
              readOnly={disabled}
            />
          </FormField>

          <FormField
            label="Business Address"
            className="md:col-span-2"
            error={errors.street?.message}
          >
            <Input
              placeholder="Enter Address"
              {...register('street')}
              disabled={disabled}
              readOnly={disabled}
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the official address used to register your business with CAC
              or other regulatory bodies. This should match your registration
              and compliance documents.
            </p>
          </FormField>

          <FormField
            label="State"
            className="md:col-span-1"
            error={errors.state?.message}
          >
            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <FormSelect
                  options={[
                    ...nigerianStates,
                    { label: 'Others', value: 'others' },
                  ]}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Select state"
                  disabled={disabled}
                />
              )}
            />
          </FormField>

          {selectedState === 'others' && (
            <FormField
              label="Specify State"
              className="md:col-span-1"
              error={errors.customState?.message}
            >
              <Input
                placeholder="Enter your state"
                {...register('customState')}
                disabled={disabled}
                readOnly={disabled}
              />
            </FormField>
          )}

          <FormField
            label="LGA/City"
            className="md:col-span-1"
            error={errors.lga?.message}
          >
            <Controller
              name="lga"
              control={control}
              render={({ field }) => (
                <FormSelect
                  options={[...cities, { label: 'Others', value: 'others' }]}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Select LGA/City"
                  disabled={disabled}
                />
              )}
            />
          </FormField>

          {selectedLga === 'others' && (
            <FormField
              label="Specify LGA/City"
              className="md:col-span-1"
              error={errors.customLga?.message}
            >
              <Input
                placeholder="Enter your LGA/City"
                {...register('customLga')}
                disabled={disabled}
                readOnly={disabled}
              />
            </FormField>
          )}

          <FormField
            label="Country"
            className="md:col-span-1"
            error={errors.country?.message}
          >
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <FormSelect
                  options={[...countries, { label: 'Others', value: 'others' }]}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Nigeria"
                  disabled={disabled}
                />
              )}
            />
          </FormField>

          {selectedCountry === 'others' && (
            <FormField
              label="Specify Country"
              className="md:col-span-1"
              error={errors.customCountry?.message}
            >
              <Input
                placeholder="Enter your country"
                {...register('customCountry')}
                disabled={disabled}
                readOnly={disabled}
              />
            </FormField>
          )}

          <FormField
            label="Office Address (Optional)"
            className="md:col-span-2"
            error={errors.officeAddress?.message}
          >
            <Input
              placeholder="Enter Office Address"
              {...register('officeAddress')}
              disabled={disabled}
              readOnly={disabled}
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the physical location where your business currently
              operates. This may be different from your registered business
              address.
            </p>
          </FormField>

          <FormField
            label="Business Industry"
            className="md:col-span-2"
            error={errors.businessIndustry?.message}
          >
            <Controller
              name="businessIndustry"
              control={control}
              render={({ field }) => (
                <FormSelect
                  options={businessIndustries}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Select Industry"
                  disabled={disabled}
                />
              )}
            />
          </FormField>

          {selectedIndustry === 'others' && (
            <FormField
              label="Specify Industry"
              className="md:col-span-2"
              error={errors.customIndustry?.message}
            >
              <Input
                placeholder="Enter your industry"
                {...register('customIndustry')}
                disabled={disabled}
                readOnly={disabled}
              />
            </FormField>
          )}

          <FormField
            label="Describe what your business does"
            className="md:col-span-2"
            error={errors.businessDescription?.message}
          >
            <Textarea
              placeholder="Enter business description"
              {...register('businessDescription')}
              className="min-h-[150px]"
              disabled={disabled}
              readOnly={disabled}
            />
          </FormField>
        </>
      )}

      {selectedBusinessType === 'unregistered' && (
        <>
          <FormField
            label="First Name"
            className="md:col-span-1"
            error={errors.firstName?.message}
          >
            <Input
              placeholder="Enter first name"
              {...register('firstName')}
              disabled={disabled}
              readOnly={disabled}
            />
          </FormField>

          <FormField
            label="Last Name"
            className="md:col-span-1"
            error={errors.lastName?.message}
          >
            <Input
              placeholder="Enter last name"
              {...register('lastName')}
              disabled={disabled}
              readOnly={disabled}
            />
          </FormField>

          <FormField
            label="Middle name"
            className="md:col-span-1"
            error={errors.middleName?.message}
          >
            <Input
              placeholder="Enter middle name"
              {...register('middleName')}
              disabled={disabled}
              readOnly={disabled}
            />
          </FormField>

          <FormField
            label="Date of birth"
            className="md:col-span-1"
            error={errors.dateOfBirth?.message}
          >
            <Input
              type="date"
              {...register('dateOfBirth')}
              disabled={disabled}
              readOnly={disabled}
            />
          </FormField>

          <FormField
            label="Email Address"
            className="md:col-span-1"
            error={errors.emailAddress?.message}
          >
            <Input
              type="email"
              placeholder="Enter email address"
              {...register('emailAddress')}
              disabled={disabled}
              readOnly={disabled}
            />
          </FormField>

          <FormField
            label="Phone Number"
            className="md:col-span-1"
            error={errors.phoneNumber?.message}
          >
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  value={field.value}
                  onChange={field.onChange}
                  inputProps={{ disabled, readOnly: disabled }}
                />
              )}
            />
          </FormField>

          <FormField
            label="Nationality"
            className="md:col-span-1"
            error={errors.nationality?.message}
          >
            <Controller
              name="nationality"
              control={control}
              render={({ field }) => (
                <FormSelect
                  options={countries}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Nigeria"
                  disabled={disabled}
                />
              )}
            />
          </FormField>

          <FormField
            label="Valid ID Number"
            className="md:col-span-1"
            error={errors.nin?.message}
          >
            <Input
              placeholder="E.g (NIN, International Passport or Driver License)"
              {...register('nin')}
              disabled={disabled}
              readOnly={disabled}
            />
          </FormField>
        </>
      )}

      {!disabled && !maxAttemptsReached && (
        <div className="md:col-span-2 flex lg:justify-end gap-3 mt-4">
          <Button
            type="submit"
            className="bg-green-600 w-full lg:w-fit hover:bg-green-700 text-white px-8"
            disabled={isVerifying || !selectedBusinessType}
          >
            {isVerifying ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      )}
    </form>
  );
};
